// Drafts list. URL-bound filter state (deep-linkable + back-button correct);
// server-side filter + pagination via the existing GET /api/admin/menu-imports.
//
// Default filter: status=draft. The dashboard's primary job is "what's
// waiting on me right now"; the unfiltered view is a click away.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import { StatusBadge } from '@/components/StatusBadge';
import {
  listMenuImports,
  type ListMenuImportsParams,
  type ListMenuImportsResponse,
  type MenuImportSort,
  type MenuImportStatus,
  type QualityDecision,
  type SourceDocKind,
} from '@/api/admin';
import { ApiError } from '@/api/client';
import {
  formatDocKind,
  formatDurationMs,
  formatRelativeTime,
  formatUsd,
} from '@/lib/format';

const PAGE_SIZE = 50;

// 'all' is a UI sentinel — it round-trips through the URL as `?status=all`
// (so back-button stays correct) and we strip it before calling the API.
// Empty string would have collided with "no param" which defaults to drafts.
const STATUS_OPTIONS: Array<{ value: 'all' | MenuImportStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
];

const DOC_KIND_OPTIONS: Array<{ value: '' | SourceDocKind; label: string }> = [
  { value: '', label: 'All' },
  { value: 'visual_menu', label: 'Visual menu' },
  { value: 'nutrition_doc', label: 'Nutrition doc' },
  { value: 'allergen_chart', label: 'Allergen chart' },
  { value: 'beverage_list', label: 'Beverage list' },
];

const SORT_OPTIONS: Array<{ value: MenuImportSort; label: string }> = [
  { value: 'recent', label: 'Newest first' },
  { value: 'priority', label: 'Review priority' },
];

function readParams(
  search: URLSearchParams,
): ListMenuImportsParams & { page: number; statusUi: 'all' | MenuImportStatus } {
  // Status param has three legitimate states:
  //   - missing       → first visit, default to "drafts" (the most common
  //                     thing an admin opens the page to look at)
  //   - 'all'         → user explicitly chose "All" in the dropdown
  //   - 'draft' | 'approved' | 'rejected' | 'failed' → explicit filter
  const rawStatus = search.get('status');
  const statusUi = (
    rawStatus === 'all' ||
    rawStatus === 'draft' ||
    rawStatus === 'approved' ||
    rawStatus === 'rejected' ||
    rawStatus === 'failed'
      ? rawStatus
      : 'draft'
  ) as 'all' | MenuImportStatus;

  const docKind = search.get('source_doc_kind');
  const pipelineVersion = search.get('pipeline_version') ?? undefined;
  const rawSort = search.get('sort');
  const sort: MenuImportSort = rawSort === 'priority' ? 'priority' : 'recent';
  const pageRaw = Number.parseInt(search.get('page') ?? '1', 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;
  return {
    // Translate the UI sentinel: 'all' → omit from the API call entirely.
    status: statusUi === 'all' ? undefined : statusUi,
    statusUi,
    source_doc_kind: (docKind as SourceDocKind | null) ?? undefined,
    pipeline_version: pipelineVersion,
    sort,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    page,
  };
}

export function DraftsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => readParams(searchParams), [searchParams]);
  const { page, statusUi, ...apiParams } = params;

  // keepPreviousData: pagination + filter changes don't blank the table out
  // mid-fetch. Combined with the visible `Loading` indicator, the table
  // stays useful while the next page arrives.
  const query = useQuery<ListMenuImportsResponse, ApiError>({
    queryKey: ['admin', 'menu-imports', apiParams],
    queryFn: ({ signal }) => listMenuImports(apiParams, signal),
    placeholderData: keepPreviousData,
  });

  function updateFilter(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // Any filter change resets pagination — staying on page 5 of the old
    // filter while the new one only has 1 page would render an empty table.
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function goToPage(newPage: number) {
    const next = new URLSearchParams(searchParams);
    if (newPage <= 1) {
      next.delete('page');
    } else {
      next.set('page', String(newPage));
    }
    setSearchParams(next, { replace: false });
  }

  const totalPages = query.data
    ? Math.max(1, Math.ceil(query.data.total / PAGE_SIZE))
    : 1;

  return (
    <AdminShell>
      <header className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Drafts</h1>
          <p className="mt-1 text-sm text-slate-500">
            {query.data
              ? `${query.data.total.toLocaleString()} ${query.data.total === 1 ? 'result' : 'results'}`
              : 'Loading…'}
          </p>
        </div>
      </header>

      <Filters
        status={statusUi}
        docKind={(searchParams.get('source_doc_kind') ?? '') as SourceDocKind | ''}
        pipelineVersion={searchParams.get('pipeline_version') ?? ''}
        sort={searchParams.get('sort') === 'priority' ? 'priority' : 'recent'}
        onChange={updateFilter}
      />

      {query.isError && (
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      )}

      <div className="mt-4">
        <DraftsTable
          rows={query.data?.rows ?? []}
          loading={query.isLoading || query.isPlaceholderData}
        />
      </div>

      {query.data && totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={goToPage} />
      )}
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------

function Filters({
  status,
  docKind,
  pipelineVersion,
  sort,
  onChange,
}: {
  status: 'all' | MenuImportStatus;
  docKind: SourceDocKind | '';
  pipelineVersion: string;
  sort: MenuImportSort;
  onChange: (key: string, value: string | null) => void;
}) {
  return (
    <div className="card p-4 flex flex-wrap items-end gap-4">
      <Field label="Status">
        <select
          className="select"
          value={status}
          onChange={(e) => onChange('status', e.target.value || null)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Sort">
        <select
          className="select"
          value={sort}
          // 'recent' is the default — write null so the URL stays clean and
          // the param only appears when the reviewer opts into priority order.
          onChange={(e) => onChange('sort', e.target.value === 'priority' ? 'priority' : null)}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Document kind">
        <select
          className="select"
          value={docKind}
          onChange={(e) => onChange('source_doc_kind', e.target.value || null)}
        >
          {DOC_KIND_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Pipeline version">
        <input
          type="text"
          className="input w-44 font-mono text-xs"
          placeholder="e.g. v2-2026-05-14"
          value={pipelineVersion}
          onChange={(e) => onChange('pipeline_version', e.target.value || null)}
        />
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------

function DraftsTable({
  rows,
  loading,
}: {
  rows: ListMenuImportsResponse['rows'];
  loading: boolean;
}) {
  if (rows.length === 0 && !loading) {
    return (
      <div className="card p-8 text-center text-slate-500">
        No imports match these filters.
      </div>
    );
  }

  return (
    <div
      className={
        'card overflow-hidden transition-opacity ' +
        (loading ? 'opacity-60' : '')
      }
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr className="text-left">
            <Th>Restaurant</Th>
            <Th>Quality</Th>
            <Th>Doc</Th>
            <Th className="text-right">Items</Th>
            <Th className="text-right">Reuse</Th>
            <Th className="text-right">Cost</Th>
            <Th className="text-right">Took</Th>
            <Th>Pipeline</Th>
            <Th>Extracted</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-50">
              <Td>
                <Link
                  to={`/drafts/${row.id}`}
                  className="font-medium text-slate-900 hover:text-accent"
                >
                  {row.restaurantName}
                </Link>
                <p className="text-xs text-slate-500">
                  <span className="font-semibold tabular-nums">Import #{row.id}</span>
                  <span className="mx-1.5">·</span>
                  <span className="font-mono">{row.restaurantSlug}</span>
                </p>
              </Td>
              <Td>
                <QualityCell
                  decision={row.qualityDecision}
                  score={row.qualityScore}
                  flags={row.qualityFlags}
                />
              </Td>
              <Td>{formatDocKind(row.sourceDocKind)}</Td>
              <Td className="text-right tabular-nums">
                {row.itemsCount.toLocaleString()}
              </Td>
              <Td
                className="text-right tabular-nums text-slate-500"
                title={row.reuseTelemetry
                  ? `${row.reuseTelemetry.exact} exact + ${row.reuseTelemetry.aliased} aliased; ${row.reuseTelemetry.created} created; ${row.reuseTelemetry.trivial} trivial; ${row.reuseTelemetry.unresolved} unresolved; ${row.reuseTelemetry.attributeAware} attribute-promoted; ${row.reuseTelemetry.attributeConflicts} attribute-blocked`
                  : 'Available after approval'}
              >
                {row.reuseTelemetry?.reuseRatio === null || !row.reuseTelemetry
                  ? '-'
                  : `${Math.round(row.reuseTelemetry.reuseRatio * 100)}%`}
              </Td>
              <Td className="text-right tabular-nums text-slate-500">
                {formatUsd(row.totalCostUsd)}
              </Td>
              <Td className="text-right tabular-nums text-slate-500">
                {formatDurationMs(row.totalDurationMs)}
              </Td>
              <Td className="font-mono text-xs text-slate-500">
                {row.pipelineVersion}
              </Td>
              <Td
                className="text-slate-500"
                title={new Date(row.extractedAt).toISOString()}
              >
                {formatRelativeTime(row.extractedAt)}
              </Td>
              <Td>
                <StatusBadge status={row.status} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// B3 — the draft-quality verdict at a glance: decision badge + score, with
// the tripped flags listed underneath so a reviewer triages without opening
// the draft. Unscored rows (null decision — pre-backfill / failed imports)
// render a muted dash so they're visibly "no verdict yet", not "clean".
const DECISION_STYLE: Record<QualityDecision, { label: string; cls: string }> = {
  publish: { label: 'Publish', cls: 'bg-emerald-100 text-emerald-800' },
  hold: { label: 'Hold', cls: 'bg-amber-100 text-amber-800' },
  reject: { label: 'Reject', cls: 'bg-rose-100 text-rose-800' },
};

function QualityCell({
  decision,
  score,
  flags,
}: {
  decision: QualityDecision | null;
  score: number | null;
  flags: string[];
}) {
  if (!decision) {
    return <span className="text-slate-300" title="Not yet scored">—</span>;
  }
  const style = DECISION_STYLE[decision];
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span
          className={
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' + style.cls
          }
        >
          {style.label}
        </span>
        {score !== null && (
          <span className="text-xs tabular-nums text-slate-500">{score.toFixed(2)}</span>
        )}
      </div>
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((flag) => (
            <span
              key={flag}
              className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-mono text-slate-600"
            >
              {flag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={
        'px-4 py-2 text-xs font-medium uppercase tracking-wider ' + className
      }
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td className={'px-4 py-3 align-top ' + className} title={title}>
      {children}
    </td>
  );
}

// ---------------------------------------------------------------------------

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-end gap-2 text-sm">
      <button
        type="button"
        className="btn-outline disabled:opacity-40"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        ← Previous
      </button>
      <span className="px-3 text-slate-600 tabular-nums">
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        className="btn-outline disabled:opacity-40"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
      >
        Next →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ErrorBanner({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  return (
    <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 flex items-center justify-between gap-4">
      <span>
        Couldn't load drafts: <code className="font-mono text-xs">{error.message}</code>
      </span>
      <button type="button" className="btn-ghost text-xs" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}
